import { useMemo } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type {
  WorkflowDefinition,
  Action,
  IfAction,
  SwitchAction,
  ForeachAction,
  UntilAction,
  AgentAction,
  AgentTool,
  Position,
} from '@/types/workflowDefinition';
import { isIfAction, isSwitchAction, isForeachAction, isUntilAction, isAgentAction } from '@/types/workflowDefinition';
import { useWorkflowStore, type LayoutOrientation } from '@/stores/workflowStore';
import { useWorkflowLayoutStore } from '@/stores/workflowLayoutStore';
import { composeWorkflowKey } from '@/stores/workflowViewPrefsStore';

export type GraphNode = Node<{
  label: string;
  actionType: string;
  actionName: string;
  kind?: string | undefined;
  cases?: string[] | undefined;
  /** Number of direct child actions in a scope (If/Switch/Foreach/Until) */
  childCount?: number | undefined;
  connectionName?: string | undefined;
  tools?: Record<string, AgentTool> | undefined;
  /** User-dragged positions for an agent's tool nodes, keyed by tool name. */
  toolNodePositions?: Record<string, Position> | undefined;
  // Tool node specific
  toolName?: string | undefined;
  description?: string | undefined;
  parentAgentId?: string | undefined;
  parentAgentName?: string | undefined;
  // Connector info for tool icons
  connectorId?: string | undefined;
  operationId?: string | undefined;
  // Agent node specific (for expand/collapse)
  isToolsExpanded?: boolean | undefined;
  onToolsExpandToggle?: ((agentName: string, expanded: boolean) => void) | undefined;
  // Agent node specific (model deployment)
  modelDeployment?: string | undefined;
  // Agent model type (e.g., 'FoundryAgentService' for Foundry agents)
  agentModelType?: string | undefined;
  // Agent harness type (e.g., 'GHCP') — present only for Coding Agents
  agentHarnessType?: string | undefined;
  // Placeholder trigger node specific
  onClick?: (() => void) | undefined;
  // Parallel branch metadata
  isForkPoint?: boolean | undefined;
  isMergePoint?: boolean | undefined;
  // Scope metadata (set on actions inside If/Switch/Foreach scopes)
  scopeParentId?: string | undefined;
  branchType?: string | undefined;
  // Scope container specific (for ForEach/Until loop body visualization)
  childNodeIds?: string[] | undefined;
  containerWidth?: number | undefined;
  containerHeight?: number | undefined;
}>;

export type GraphEdge = Edge;

type ConversionResult = {
  nodes: GraphNode[];
  edges: GraphEdge[];
};

type FlattenedAction = {
  name: string;
  /** Scope-qualified ID for unique node identification (e.g., "ForEach_1/Compose") */
  scopedId: string;
  action: Action;
  parentId?: string | undefined;
  branchType?: 'true' | 'false' | 'default' | string | undefined; // For If/Switch/Foreach branches
};

/**
 * Count the number of direct child actions in a scope
 */
const countChildActions = (action: Action): number => {
  let count = 0;

  if (isIfAction(action)) {
    const ifAction = action as IfAction;
    if (ifAction.actions) {
      count += Object.keys(ifAction.actions).length;
    }
    if (ifAction.else?.actions) {
      count += Object.keys(ifAction.else.actions).length;
    }
  } else if (isSwitchAction(action)) {
    const switchAction = action as SwitchAction;
    if (switchAction.cases) {
      for (const caseData of Object.values(switchAction.cases)) {
        if (caseData && typeof caseData === 'object' && 'actions' in caseData && caseData.actions) {
          count += Object.keys(caseData.actions).length;
        }
      }
    }
    if (switchAction.default?.actions) {
      count += Object.keys(switchAction.default.actions).length;
    }
  } else if (isForeachAction(action) || isUntilAction(action)) {
    const loopAction = action as ForeachAction | UntilAction;
    if (loopAction.actions) {
      count += Object.keys(loopAction.actions).length;
    }
  }

  return count;
};

/**
 * Extract connection name from action/trigger inputs
 */
const extractConnectionName = (inputs: Record<string, unknown> | undefined): string | undefined => {
  if (!inputs) return undefined;

  // Check for service provider connection
  const serviceProviderConfig = inputs.serviceProviderConfiguration as Record<string, unknown> | undefined;
  if (serviceProviderConfig?.connectionName) {
    return serviceProviderConfig.connectionName as string;
  }

  // Check for managed API connection
  const host = inputs.host as Record<string, unknown> | undefined;
  const connection = host?.connection as Record<string, unknown> | undefined;
  if (connection?.referenceName) {
    return connection.referenceName as string;
  }

  // Check for agent connection (modelConfigurations.model1.referenceName)
  const modelConfigurations = inputs.modelConfigurations as Record<string, unknown> | undefined;
  const model1 = modelConfigurations?.model1 as Record<string, unknown> | undefined;
  if (model1?.referenceName) {
    return model1.referenceName as string;
  }

  return undefined;
};

/**
 * Converts a workflow definition to XY Flow graph format.
 *
 * `nodePositions` (React-Flow-style ids → positions) and `toolNodePositions`
 * (agent name → tool name → position) are sourced from the client-side
 * layout store. Both are optional so this function stays usable in contexts
 * that don't have a store yet (e.g., subworkflow tool preview), where it
 * falls back to any positions embedded in `metadata` and then to a lazy
 * computed position.
 */
export const convertWorkflowToGraph = (
  workflow: WorkflowDefinition,
  orientation: LayoutOrientation = 'horizontal',
  nodePositions: Record<string, Position> = {},
  toolNodePositions: Record<string, Record<string, Position>> = {},
): ConversionResult => {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];

  // Validate that definition exists
  if (!workflow.definition) {
    console.warn('[convertWorkflowToGraph] workflow.definition is missing', { workflow });
    return { nodes: [], edges: [] };
  }

  // Process triggers
  const triggers = Object.entries(workflow.definition.triggers ?? {});
  const triggerNodes: GraphNode[] = triggers.map(([name, trigger], index) => {
    const connectionName = extractConnectionName(trigger.inputs as Record<string, unknown> | undefined);
    return {
      id: `trigger-${name}`,
      type: 'trigger',
      position:
        nodePositions[`trigger-${name}`]
        ?? trigger.metadata?.position
        ?? computeLazyPosition(index, orientation),
      data: {
        label: name,
        actionType: trigger.type,
        actionName: name,
        ...(trigger.kind ? { kind: trigger.kind } : {}),
        ...(connectionName ? { connectionName } : {}),
      },
    };
  });
  nodes.push(...triggerNodes);

  // Flatten all actions (including nested ones)
  const flattenedActions = flattenActions(workflow.definition.actions ?? {});

  // Create nodes for all actions
  const actionNodes: GraphNode[] = flattenedActions.map((item, index) => {
    const { name, scopedId, action, parentId, branchType } = item;
    const nodeId = `action-${scopedId}`;
    const connectionName = extractConnectionName(action.inputs as Record<string, unknown> | undefined);

    let nodeType = 'action';
    const nodeData: {
      label: string;
      actionType: string;
      actionName: string;
      cases?: string[] | undefined;
      childCount?: number | undefined;
      connectionName?: string | undefined;
      tools?: Record<string, AgentTool> | undefined;
      toolNodePositions?: Record<string, Position> | undefined;
      modelDeployment?: string | undefined;
      agentModelType?: string | undefined;
      agentHarnessType?: string | undefined;
      scopeParentId?: string | undefined;
      branchType?: string | undefined;
    } = {
      label: name,
      actionType: action.type,
      actionName: name,
      ...(connectionName ? { connectionName } : {}),
      ...(parentId ? { scopeParentId: parentId } : {}),
      ...(branchType ? { branchType } : {}),
    };

    if (isIfAction(action)) {
      nodeType = 'if';
      nodeData.childCount = countChildActions(action);
    }
    if (isSwitchAction(action)) {
      nodeType = 'switch';
      nodeData.cases = action.cases ? Object.keys(action.cases) : [];
      nodeData.childCount = countChildActions(action);
    }
    if (isForeachAction(action)) {
      nodeType = 'foreach';
      nodeData.childCount = countChildActions(action);
    }
    if (isUntilAction(action)) {
      nodeType = 'foreach'; // Until uses same visual as Foreach (scope with loop body)
      nodeData.childCount = countChildActions(action);
    }
    if (isAgentAction(action)) {
      nodeType = 'agent';
      const agentAction = action as AgentAction;
      if (agentAction.tools) {
        nodeData.tools = agentAction.tools;
      }
      // Pass through custom tool-node positions so the canvas can honor user drags.
      // Prefer the layout store entry (current authority), then fall back to any
      // legacy positions still embedded in metadata.
      const agentToolPositions =
        toolNodePositions[name] ?? agentAction.metadata?.toolNodePositions;
      if (agentToolPositions && Object.keys(agentToolPositions).length > 0) {
        nodeData.toolNodePositions = agentToolPositions;
      }
      // Extract model deployment from inputs.parameters.deploymentId or inputs.parameters.modelId
      const agentInputs = agentAction.inputs as {
        agentModelType?: string;
        parameters?: {
          deploymentId?: string;
          modelId?: string;
          agentModelType?: string;
          agentModelSettings?: { agentHarness?: { type?: string } };
        };
        agentModelSettings?: { agentHarness?: { type?: string } };
      } | undefined;
      const modelDeployment = agentInputs?.parameters?.deploymentId || agentInputs?.parameters?.modelId;
      if (modelDeployment) {
        nodeData.modelDeployment = modelDeployment;
      }
      // Pass agentModelType for visual differentiation (e.g., Foundry = purple).
      // The value can live at the top level (set at creation via defaultInputs)
      // or nested under parameters (after serialization/save) — mirror the
      // dual-location lookup that `isFoundryAgent` performs.
      const agentModelType = agentInputs?.agentModelType ?? agentInputs?.parameters?.agentModelType;
      if (agentModelType) {
        nodeData.agentModelType = agentModelType;
      }
      // Pass agentHarnessType so the canvas can color/icon Coding Agents
      // distinctly. Same dual-location lookup as agentModelType.
      const agentHarnessType =
        agentInputs?.parameters?.agentModelSettings?.agentHarness?.type ??
        agentInputs?.agentModelSettings?.agentHarness?.type;
      if (agentHarnessType) {
        nodeData.agentHarnessType = agentHarnessType;
      }
    }

    return {
      id: nodeId,
      type: nodeType,
      position:
        nodePositions[nodeId]
        ?? action.metadata?.position
        ?? computeLazyPosition(index + triggerNodes.length, orientation),
      data: nodeData,
    };
  });
  nodes.push(...actionNodes);

  // Create scope container nodes for ForEach/Until loop bodies
  const foreachItems = flattenedActions.filter(
    (item) => isForeachAction(item.action) || isUntilAction(item.action)
  );

  for (const foreachItem of foreachItems) {
    const foreachNodeId = `action-${foreachItem.scopedId}`;
    const loopChildIds = flattenedActions
      .filter((item) => item.parentId === foreachNodeId && item.branchType === 'loop')
      .map((item) => `action-${item.scopedId}`);

    if (loopChildIds.length > 0) {
      nodes.push({
        id: `scope-container-${foreachItem.scopedId}`,
        type: 'scopeContainer',
        position: { x: 0, y: 0 },
        data: {
          label: `${foreachItem.name} loop body`,
          actionType: 'scopeContainer',
          actionName: `scope-container-${foreachItem.scopedId}`,
          childNodeIds: loopChildIds,
          containerWidth: 0,
          containerHeight: 0,
        },
        draggable: false,
        selectable: false,
        connectable: false,
        focusable: false,
        zIndex: -1,
      });
    }
  }

  // Create edges based on dependencies
  // Build a lookup by (parentId, name) for resolving runAfter within scopes
  const actionMap = new Map(flattenedActions.map((item) => [item.name, item]));
  // Map from (parentId + '|' + simpleName) → FlattenedAction for resolving runAfter within scopes
  const scopedLookup = new Map(flattenedActions.map((item) => [item.parentId ? `${item.parentId}|${item.name}` : item.name, item]));
  const triggerMap = new Map(triggerNodes.map((node) => [node.data.actionName, node]));

  // Edges from triggers to first actions (only top-level actions without runAfter)
  // Skip actions marked as detached (free-floating nodes)
  // IMPORTANT: Differentiate between:
  //   - runAfter: undefined/missing -> runs after trigger
  //   - runAfter: {} (empty object) + no detached flag -> also runs after trigger
  //   - runAfter: {} (empty object) + detached: true -> detached/floating
  //   - detached: true -> explicitly detached (internal flag, removed on save)
  const firstActions = flattenedActions.filter((item) => {
    const runAfter = item.action.runAfter;
    const hasNoDependencies = !runAfter || Object.keys(runAfter).length === 0;
    const isTopLevel = !item.parentId;
    const isDetached = item.action.detached === true;
    return hasNoDependencies && isTopLevel && !isDetached;
  });

  triggerNodes.forEach((triggerNode) => {
    firstActions.forEach((firstAction) => {
      const targetId = `action-${firstAction.scopedId}`;
      edges.push({
        id: `${triggerNode.id}->${targetId}`,
        source: triggerNode.id,
        target: targetId,
      });
    });
  });

  // Edges based on runAfter dependencies
  flattenedActions.forEach((item) => {
    const { scopedId, action, parentId, branchType } = item;
    const targetId = `action-${scopedId}`;

    // Handle branch edges (from If/Switch/Foreach to their children)
    // These edges use 'dotted' type to show parent-child relationship
    // Only create branch edge if the action has NO runAfter dependencies
    // (i.e., it's a "first" action in the branch). Actions with runAfter
    // will be connected via their runAfter edges instead.
    const hasRunAfterDeps = action.runAfter && Object.keys(action.runAfter).length > 0;
    if (parentId && branchType && !hasRunAfterDeps) {
      let sourceHandle: string | null = null;

      if (branchType === 'true' || branchType === 'false') {
        sourceHandle = branchType;
      } else if (branchType === 'loop' || branchType === 'done') {
        sourceHandle = branchType;
      } else if (branchType.startsWith('case-')) {
        sourceHandle = branchType;
      } else if (branchType === 'default') {
        sourceHandle = 'default';
      }

      const edge: GraphEdge = {
        id: `${parentId}->${targetId}`,
        source: parentId,
        target: targetId,
        type: 'dotted', // Use dotted edge for parent-child relationships
      };

      if (sourceHandle) {
        edge.sourceHandle = sourceHandle;
      }

      edges.push(edge);
    }

    // Handle runAfter edges
    const runAfter = action.runAfter;
    if (runAfter) {
      Object.keys(runAfter).forEach((dependencyName) => {
        // Check if dependency is a trigger or an action
        const isTrigger = triggerMap.has(dependencyName);
        // Resolve dependency within the same scope first, then fall back to global
        const scopeKey = parentId ? `${parentId}|${dependencyName}` : dependencyName;
        const dependency = scopedLookup.get(scopeKey) ?? actionMap.get(dependencyName);
        const sourceId = isTrigger ? `trigger-${dependencyName}` : `action-${dependency?.scopedId ?? dependencyName}`;

        // Determine sourceHandle based on parent action type
        let sourceHandle: string | null = null;

        if (dependency) {
          const depAction = dependency.action;

          if (isForeachAction(depAction) || isUntilAction(depAction)) {
            // Edge from Foreach/Until to action after it should use 'done' handle
            sourceHandle = 'done';
          } else if (isIfAction(depAction) || isSwitchAction(depAction)) {
            // Edge from If/Switch to action after it should use 'merge' handle
            // to prevent false parallelism detection in layout engine.
            // 'merge' maps to lane 0 (center), unlike 'done' which maps to lane 1.
            sourceHandle = 'merge';
          }
        }

        const edge: GraphEdge = {
          id: `${sourceId}->${targetId}`,
          source: sourceId,
          target: targetId,
          // Use dotted edge for actions inside ForEach/Until loop bodies
          ...(branchType === 'loop' ? { type: 'dotted' as const } : {}),
        };

        if (sourceHandle) {
          edge.sourceHandle = sourceHandle;
        }

        edges.push(edge);
      });
    }
  });

  // Annotate fork/merge metadata on nodes.
  // Fork point: node with 2+ outgoing runAfter edges (without sourceHandle).
  // Merge point: action with 2+ entries in its runAfter.
  const outgoingCount = new Map<string, number>();
  edges.forEach((edge) => {
    // Only count non-dotted edges (dotted = scope edges from If/Switch/Foreach)
    if (edge.type !== 'dotted') {
      outgoingCount.set(edge.source, (outgoingCount.get(edge.source) ?? 0) + 1);
    }
  });

  for (const node of nodes) {
    const outCount = outgoingCount.get(node.id) ?? 0;
    if (outCount >= 2) {
      node.data = { ...node.data, isForkPoint: true };
    }
  }

  // Mark merge points: actions where runAfter has 2+ entries
  const nodesById = new Map(nodes.map((n) => [n.id, n] as const));
  flattenedActions.forEach((item) => {
    const runAfter = item.action.runAfter;
    if (runAfter && Object.keys(runAfter).length >= 2) {
      const nodeId = `action-${item.scopedId}`;
      const node = nodesById.get(nodeId);
      if (node) {
        node.data = { ...node.data, isMergePoint: true };
      }
    }
  });

  return { nodes, edges };
};

/**
 * Flattens nested actions into a flat array, preserving parent relationships.
 * Each action gets a scopedId that is unique across all scopes (e.g., "ForEach_1/Compose").
 */
const flattenActions = (
  actions: Record<string, Action>,
  parentId?: string,
  branchType?: string,
  scopePrefix?: string
): FlattenedAction[] => {
  const result: FlattenedAction[] = [];

  for (const [name, action] of Object.entries(actions)) {
    const scopedId = scopePrefix ? `${scopePrefix}/${name}` : name;
    // Add the action itself
    result.push({
      name,
      scopedId,
      action,
      parentId,
      branchType,
    });

    // Recursively flatten nested actions
    if (isIfAction(action)) {
      const actionId = `action-${scopedId}`;
      const ifAction = action as IfAction;

      // Flatten true branch
      if (ifAction.actions) {
        result.push(...flattenActions(ifAction.actions, actionId, 'true', scopedId));
      }

      // Flatten false branch
      if (ifAction.else?.actions) {
        result.push(...flattenActions(ifAction.else.actions, actionId, 'false', scopedId));
      }
      continue;
    }

    if (isSwitchAction(action)) {
      const actionId = `action-${scopedId}`;
      const switchAction = action as SwitchAction;

      // Flatten each case (check if cases exists)
      if (switchAction.cases && typeof switchAction.cases === 'object') {
        for (const [caseName, caseData] of Object.entries(switchAction.cases)) {
          if (caseData && typeof caseData === 'object' && 'actions' in caseData && caseData.actions) {
            result.push(...flattenActions(caseData.actions, actionId, `case-${caseName}`, scopedId));
          }
        }
      }

      // Flatten default case
      if (switchAction.default?.actions) {
        result.push(...flattenActions(switchAction.default.actions, actionId, 'default', scopedId));
      }
      continue;
    }

    if (isForeachAction(action)) {
      const actionId = `action-${scopedId}`;
      const foreachAction = action as ForeachAction;

      // Flatten loop body
      if (foreachAction.actions) {
        result.push(...flattenActions(foreachAction.actions, actionId, 'loop', scopedId));
      }
      continue;
    }

    if (isUntilAction(action)) {
      const actionId = `action-${scopedId}`;
      const untilAction = action as UntilAction;

      // Flatten loop body
      if (untilAction.actions) {
        result.push(...flattenActions(untilAction.actions, actionId, 'loop', scopedId));
      }
      continue;
    }
  }

  return result;
};

/**
 * Computes a lazy position for nodes without explicit positions
 * Uses layout based on orientation
 */
const computeLazyPosition = (index: number, orientation: LayoutOrientation): { x: number; y: number } => {
  if (orientation === 'horizontal') {
    // Left-to-right layout
    return {
      x: index * 280,
      y: 150,
    };
  }
  // Top-to-bottom layout (vertical)
  return {
    x: 250,
    y: index * 150,
  };
};

/**
 * Hook that converts a workflow definition to XY Flow graph format
 * Returns memoized nodes and edges.
 *
 * `positionsOverride` lets monitoring mode pass its own snapshot-local
 * position map. When provided, it bypasses the layout store entirely (the
 * snapshot's structure may differ from the editable workflow, so the
 * design-mode layout would be wrong).
 */
export const useWorkflowGraph = (
  workflow: WorkflowDefinition | null,
  positionsOverride?: Record<string, Position>,
): ConversionResult => {
  const orientation = useWorkflowStore((state) => state.layoutOrientation);
  const identity = useWorkflowStore((state) => state.currentWorkflowIdentity);
  const mode = useWorkflowStore((state) => state.currentMode);
  // Subscribe to the layout slice for the current workflow + mode so position
  // drags re-render the canvas without re-deriving the graph when the
  // workflow JSON itself hasn't changed.
  const layout = useWorkflowLayoutStore((state) =>
    identity ? state.byWorkflow[composeWorkflowKey(identity)]?.[mode] : undefined
  );

  return useMemo(() => {
    if (!workflow?.definition) {
      return { nodes: [], edges: [] };
    }

    const nodePositions = positionsOverride ?? layout?.nodePositions;
    const toolPositions = positionsOverride ? {} : layout?.toolNodePositions;

    return convertWorkflowToGraph(
      workflow,
      orientation,
      nodePositions,
      toolPositions,
    );
  }, [workflow, orientation, layout, positionsOverride]);
};
